import { type CSSProperties, useEffect, useState } from 'react'
import { Play, Square, Radio, Users, Music, RefreshCw } from 'lucide-react'
import {
  getChannelStreamStatus,
  getNowPlayingTrack,
  resolveMediaUrl,
  startChannelStream,
  stopChannelStream,
  type Channel,
  type NowPlayingTrack,
  type StreamStatusResponse
} from '../../../api'
import { Button, IconButton } from '../../../components/ui'
import styles from './StreamControlPanel.module.css'
import streamIndicators from '../styles/streamIndicators.module.css'

type StreamControlPanelProps = {
  channel: Channel
}

const formatDuration = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatUptime = (startedAt: string): string => {
  const start = new Date(startedAt).getTime()
  const now = Date.now()
  const diff = Math.max(0, now - start)

  return formatDuration(diff / 1000)
}

const getStatusColor = (status: StreamStatusResponse['status']): string => {
  switch (status) {
    case 'live':
      return '#22c55e'
    case 'stopped':
      return '#6b7280'
    case 'error':
      return '#ef4444'
  }
}

const getStatusLabel = (status: StreamStatusResponse['status']): string => {
  switch (status) {
    case 'live':
      return 'Live'
    case 'stopped':
      return 'Stopped'
    case 'error':
      return 'Error'
  }
}

export const StreamControlPanel = ({ channel }: StreamControlPanelProps) => {
  const [streamStatus, setStreamStatus] = useState<StreamStatusResponse | null>(null)
  const [nowPlaying, setNowPlaying] = useState<NowPlayingTrack | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<'start' | 'stop' | null>(null)
  const [uptime, setUptime] = useState<string>('')

  const fetchStatus = async (): Promise<void> => {
    try {
      setLoading(true)
      const status = await getChannelStreamStatus(channel.id)
      setStreamStatus(status)

      if (status.current_track) {
        try {
          const track = await getNowPlayingTrack(channel.slug)
          setNowPlaying(track)
        } catch {
          setNowPlaying(null)
        }
      } else {
        setNowPlaying(null)
      }
    } catch (error) {
      console.error('Failed to fetch stream status:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchStatus()
  }, [channel.id])

  useEffect(() => {
    if (!streamStatus?.current_track?.started_at) {
      setUptime('')

      return
    }

    const updateUptime = (): void => {
      setUptime(formatUptime(streamStatus.current_track!.started_at))
    }

    updateUptime()
    const interval = setInterval(updateUptime, 1000)

    return () => clearInterval(interval)
  }, [streamStatus?.current_track?.started_at])

  useEffect(() => {
    if (!streamStatus || streamStatus.status !== 'live') {
      return
    }

    const interval = setInterval(() => {
      void fetchStatus()
    }, 10000)

    return () => clearInterval(interval)
  }, [streamStatus?.status])

  const handleStart = async (): Promise<void> => {
    try {
      setActionLoading('start')
      await startChannelStream(channel.id)
      await fetchStatus()
    } catch (error) {
      console.error('Failed to start stream:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleStop = async (): Promise<void> => {
    try {
      setActionLoading('stop')
      await stopChannelStream(channel.id)
      await fetchStatus()
    } catch (error) {
      console.error('Failed to stop stream:', error)
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className={[styles.panel, styles.loading].join(' ')}>
        <RefreshCw size={20} className={styles.spin} />
        <span>Loading stream status...</span>
      </div>
    )
  }

  if (!streamStatus) {
    return (
      <div className={[styles.panel, styles.error].join(' ')}>
        <span>Failed to load stream status</span>
        <Button onClick={() => void fetchStatus()}>
          Retry
        </Button>
      </div>
    )
  }

  const statusColor = getStatusColor(streamStatus.status)
  const statusLabel = getStatusLabel(streamStatus.status)

  return (
    <div className={styles.panel}>
      <div className={styles.statusHeader}>
        <div className={styles.statusIndicator} style={{ '--status-color': statusColor } as CSSProperties}>
          <span className={styles.statusDot} />
          <span className={styles.statusLabel}>{statusLabel}</span>
        </div>
        <div className={styles.statusActions}>
          <IconButton
            icon={<RefreshCw size={16} />}
            label="Refresh status"
            onClick={() => void fetchStatus()}
            title="Refresh status"
          />
          {streamStatus.status === 'live' ? (
            <Button
              disabled={actionLoading !== null}
              onClick={() => void handleStop()}
              variant="danger"
            >
              <Square size={14} />
              {actionLoading === 'stop' ? 'Stopping...' : 'Stop'}
            </Button>
          ) : (
            <Button
              disabled={actionLoading !== null}
              onClick={() => void handleStart()}
              variant="primary"
            >
              <Play size={14} />
              {actionLoading === 'start' ? 'Starting...' : 'Start'}
            </Button>
          )}
        </div>
      </div>

      {streamStatus.status === 'live' && (
        <>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <Users size={14} />
              <span>{streamStatus.listeners} listeners</span>
            </div>
            {uptime && (
              <div className={styles.stat}>
                <Radio size={14} />
                <span>{uptime}</span>
              </div>
            )}
          </div>

          {nowPlaying && (
            <div className={styles.nowPlaying}>
              <div className={styles.artwork}>
                {nowPlaying.image_derivatives.length > 0 && nowPlaying.image_derivatives[0] ? (
                  <img
                    alt={nowPlaying.title}
                    src={resolveMediaUrl(nowPlaying.image_derivatives[0].url)}
                  />
                ) : (
                  <div className={styles.artworkPlaceholder}>
                    <Music size={20} />
                  </div>
                )}
              </div>
              <div className={styles.nowPlayingInfo}>
                <div className={styles.nowPlayingTitle}>
                  <span>{nowPlaying.title}</span>
                  {nowPlaying.source_quality === 'flac' && <span className={streamIndicators.qualityBadge}>HQ</span>}
                </div>
                {nowPlaying.artist && (
                  <div className={styles.nowPlayingArtist}>{nowPlaying.artist}</div>
                )}
              </div>
            </div>
          )}

          <div className={styles.url}>
            <span className={styles.urlLabel}>Stream URL:</span>
            <code className={styles.urlValue}>{streamStatus.stream_url}</code>
          </div>
        </>
      )}

      {streamStatus.status === 'stopped' && (
        <div className={styles.stoppedMessage}>
          <p>Stream is not currently broadcasting.</p>
          <p className={styles.stoppedHint}>
            Click Start to begin streaming audio from this channel.
          </p>
        </div>
      )}

      {streamStatus.status === 'error' && (
        <div className={styles.errorMessage}>
          <p>Stream encountered an error.</p>
          <p className={styles.errorHint}>
            Check Liquidsoap logs and Icecast status for details.
          </p>
        </div>
      )}
    </div>
  )
}
